import { action, flow, makeObservable, observable } from 'mobx';

import DEFAULT_INDICATORS_SETTINGS_VALUES from 'src/api/chart/default-indicators-settings.json';
import type { IndicatorsView } from 'src/api/chart/drilling-plan-charts-api';
import { UserSettingsManager } from 'src/api/user-settings';
import type { Directories } from 'src/store/directories/directories.store';
import type { EditingStore } from 'src/store/editing/editing-store';
import type { NotificationsStore } from 'src/store/notifications-store/notifications-store';
import type { ViewsStore } from 'src/store/views';

import type { IndicatorSettings } from '../../presets/indicators-view-settings-sidebar/entities';
import { IndicatorsSettings } from '../../presets/indicators-view-settings-sidebar/entities';
import type { Viewport } from '../../shared/viewport/viewport';

export class IndicatorsTableSettingsStore {
  private readonly views: ViewsStore;
  private readonly directories: Directories;
  private readonly notifications: NotificationsStore;

  private readonly viewSettingsManager: UserSettingsManager<IndicatorsView.SettingsValues>;

  @observable isLoading = false;
  @observable settings?: IndicatorsSettings;

  constructor(
    horizontalViewport: Viewport,
    editing: EditingStore,
    views: ViewsStore,
    directories: Directories,
    notifications: NotificationsStore,
  ) {
    this.views = views;
    this.directories = directories;
    this.notifications = notifications;

    this.viewSettingsManager = new UserSettingsManager<IndicatorsView.SettingsValues>(
      'graphIndicators',
      DEFAULT_INDICATORS_SETTINGS_VALUES,
    );

    makeObservable(this);
  }

  @flow.bound
  private async *loadIndicatorsView() {
    try {
      this.isLoading = true;

      const [indicatorsSettingsView, indicatorsSettingsValues] = await Promise.all([
        this.views.indicatorsSettingsView.loadView(),
        this.viewSettingsManager.getOrCreate(),
      ]);
      yield;

      let effectiveSettingsValues: IndicatorsView.SettingsValues;

      if (!indicatorsSettingsValues?.table?.items) {
        await this.viewSettingsManager.create();

        const newSettingsValues = await this.viewSettingsManager.get();
        yield;

        effectiveSettingsValues = newSettingsValues;
      } else {
        effectiveSettingsValues = indicatorsSettingsValues;
      }

      // Persisted values may miss indicators that exist in the view (e.g. the view
      // was extended with a new indicator after the user's settings were saved, or
      // the default-settings file was updated). Without reconciliation the table
      // renders only the persisted indicators, while the "Edit order" sidebar
      // shows the missing ones as enabled — so the row count diverges until the
      // user clicks "Apply". Reconcile and persist the missing indicators here.
      const reconciledSettingsValues = reconcileMissingIndicators(indicatorsSettingsView, effectiveSettingsValues);

      if (reconciledSettingsValues !== effectiveSettingsValues) {
        await this.viewSettingsManager.update(reconciledSettingsValues);
        yield;
      }

      this.settings = new IndicatorsSettings(indicatorsSettingsView, reconciledSettingsValues, this.directories);
    } catch (e) {
      yield;

      this.notifications.showErrorMessageT('errors:failedToLoadIndicatorsTableSettings');
    } finally {
      this.isLoading = false;
    }
  }

  @flow.bound
  private async *updateIndicatorsValues(indicatorsValues: IndicatorsView.SettingsValues, onSuccess?: VoidFunction) {
    try {
      this.isLoading = true;

      const indicatorsSettings = this.settings;

      if (indicatorsSettings) {
        await this.viewSettingsManager.update(indicatorsValues);
        yield;

        indicatorsSettings.setValues(indicatorsValues);
        onSuccess?.();
      }
    } catch (e) {
      yield;

      console.error(e);
      this.notifications.showErrorMessageT('errors:failedToUpdateIndicatorsTableSettings');
    } finally {
      this.isLoading = false;
    }
  }

  updateIndicators(indicatorsSettings: IndicatorSettings[], onSuccess?: VoidFunction): void {
    const updatedSettingsValues: IndicatorsView.SettingsValues = {
      table: {
        items: indicatorsSettings.map(({ isShown, name }) => ({ fieldId: name, isShown })),
      },
    };

    this.updateIndicatorsValues(updatedSettingsValues, onSuccess);
  }

  @action.bound
  init(): void {
    this.loadIndicatorsView();
  }
}

function reconcileMissingIndicators(
  view: IndicatorsView.Settings,
  values: IndicatorsView.SettingsValues,
): IndicatorsView.SettingsValues {
  const savedItems = values.table?.items ?? [];
  const savedFieldIds = new Set(savedItems.map(({ fieldId }) => fieldId));

  const missingItems = view.table.items
    .filter(({ fieldId }) => !savedFieldIds.has(fieldId))
    .map<IndicatorsView.SettingsValue>(({ fieldId }) => ({ fieldId, isShown: true }));

  if (!missingItems.length) {
    return values;
  }

  return {
    table: {
      items: [...savedItems, ...missingItems],
    },
  };
}
